import { useEffect, useRef, useState } from 'react';
import {
  claimPersistentUpload,
  enqueuePersistentUploads,
  listPersistentUploads,
  removePersistentUpload,
  updatePersistentUpload,
  uploadScope,
  type PersistentUpload,
} from '@tov-plus/pwa';
import { msg, useToast } from '@tov-plus/ui';
import { guestApi } from '../app/api';
import { guestEnv } from '../config/env';
import { BrowserHttpError } from '../lib/http/createBrowserHttpClient';
import { uploadToSignedUrl } from '../lib/uploads/uploadToSignedUrl';

export function useGalleryUploadQueue(input: {
  occasionSlug: string;
  guestToken: string;
  enabled: boolean;
  onUploaded: () => Promise<void> | void;
}) {
  const [uploads, setUploads] = useState<PersistentUpload[]>([]);
  const [scope, setScope] = useState<string | null>(null);
  const active = useRef<{ id: string; controller: AbortController } | null>(
    null,
  );
  const callbacks = useRef(input);
  callbacks.current = input;
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  useEffect(() => {
    let disposed = false;
    void uploadScope(input.occasionSlug, input.guestToken).then((value) => {
      if (!disposed) setScope(value);
    });
    return () => {
      disposed = true;
      setScope(null);
      setUploads([]);
    };
  }, [input.occasionSlug, input.guestToken]);

  useEffect(() => {
    if (!scope || !input.enabled) return;
    let disposed = false;
    let busy = false;
    const refresh = async () => {
      const next = await listPersistentUploads(scope);
      if (!disposed) setUploads(next);
    };
    const tick = async () => {
      if (disposed || busy) return;
      busy = true;
      let upload: PersistentUpload | null = null;
      let heartbeat: ReturnType<typeof setInterval> | undefined;
      try {
        await refresh();
        if (!navigator.onLine) return;
        upload = await claimPersistentUpload(scope);
        if (!upload) return;
        const current = upload;
        const controller = new AbortController();
        active.current = { id: current.id, controller };
        const update = async (patch: Partial<PersistentUpload>) => {
          const next = await updatePersistentUpload(
            current.id,
            patch,
            current.leaseOwner,
          );
          if (!next) {
            controller.abort();
            throw new DOMException('Upload paused or removed', 'AbortError');
          }
          Object.assign(current, next);
        };
        heartbeat = setInterval(() => {
          void update({ leaseUntil: Date.now() + 180_000 }).catch(() =>
            controller.abort(),
          );
        }, 20_000);
        if (!current.checksum) {
          const digest = await crypto.subtle.digest(
            'SHA-256',
            await current.file.arrayBuffer(),
          );
          await update({
            checksum: Array.from(new Uint8Array(digest), (byte) =>
              byte.toString(16).padStart(2, '0'),
            ).join(''),
          });
        }
        const session = await guestApi.requestGalleryUploadUrl(
          callbacks.current.occasionSlug,
          callbacks.current.guestToken,
          {
            eventId: current.eventId,
            albumId: current.albumId,
            fileName: current.fileName,
            mimeType: current.mimeType,
            fileSizeBytes: current.file.size,
            caption: current.caption,
            checksumSha256: current.checksum,
            resumable: true,
            resumeSessionId: current.sessionId,
          },
        );
        await update({ sessionId: session.id });
        if (session.status !== 'completed') {
          if (session.multipart) {
            const partSize = session.multipart.partSizeBytes;
            for (const part of session.multipart.parts) {
              if (
                current.parts.some(
                  (saved) => saved.partNumber === part.partNumber,
                )
              )
                continue;
              const offset = (part.partNumber - 1) * partSize;
              const blob = current.file.slice(offset, offset + partSize);
              const etag = await uploadToSignedUrl({
                url: part.signedUploadUrl,
                file: blob,
                signal: controller.signal,
                onProgress: (percent) =>
                  setUploads((items) =>
                    items.map((item) =>
                      item.id === current.id
                        ? {
                            ...item,
                            progress: Math.round(
                              ((offset + (blob.size * percent) / 100) /
                                current.file.size) *
                                100,
                            ),
                          }
                        : item,
                    ),
                  ),
              });
              if (!etag)
                throw new Error('Storage CORS must expose the ETag header.');
              await update({
                parts: [
                  ...current.parts,
                  { partNumber: part.partNumber, etag },
                ],
                progress: Math.round(
                  ((offset + blob.size) / current.file.size) * 100,
                ),
              });
            }
          } else {
            await uploadToSignedUrl({
              url: new URL(
                session.signedUploadUrl,
                guestEnv.apiBaseUrl,
              ).toString(),
              file: current.file,
              headers: session.uploadHeaders,
              contentType: current.mimeType,
              signal: controller.signal,
              onProgress: (progress) =>
                setUploads((items) =>
                  items.map((item) =>
                    item.id === current.id ? { ...item, progress } : item,
                  ),
                ),
            });
          }
          if (controller.signal.aborted || disposed)
            throw new DOMException('Upload paused', 'AbortError');
          await guestApi.completeGalleryUpload(
            callbacks.current.occasionSlug,
            callbacks.current.guestToken,
            session.id,
            session.multipart ? current.parts : undefined,
          );
        }
        await removePersistentUpload(current.id, current.leaseOwner);
        upload = null;
        toastRef.current.success(
          msg(
            'pages.gallery_upload_queue.upload_received_processing_and_review_continue_on_server',
          ),
        );
        await Promise.resolve(callbacks.current.onUploaded()).catch(
          () => undefined,
        );
      } catch (error) {
        if (upload) {
          const attempts = navigator.onLine
            ? upload.attempts + 1
            : upload.attempts;
          const permanent =
            error instanceof BrowserHttpError &&
            error.statusCode >= 400 &&
            error.statusCode < 500 &&
            ![408, 429].includes(error.statusCode);
          const failed = permanent || attempts >= 5;
          await updatePersistentUpload(
            upload.id,
            {
              status: failed ? 'failed' : 'queued',
              attempts,
              retryAt:
                Date.now() +
                Math.min(60_000, 1000 * 2 ** attempts) +
                Math.random() * 1000,
              leaseUntil: 0,
            },
            upload.leaseOwner,
          ).catch(() => null);
          if (failed && !disposed)
            toastRef.current.error(
              msg(
                'pages.gallery_upload_queue.upload_needs_attention_your_file_is_still_saved_in_this_browser',
              ),
            );
        } else if (!disposed)
          toastRef.current.error(
            msg(
              'pages.gallery_upload_queue.browser_upload_storage_is_unavailable',
            ),
          );
      } finally {
        if (heartbeat) clearInterval(heartbeat);
        active.current = null;
        busy = false;
        if (!disposed) await refresh().catch(() => undefined);
      }
    };
    const interval = setInterval(() => {
      void tick();
    }, 1500);
    const reconnect = () => {
      void tick();
    };
    window.addEventListener('online', reconnect);
    void tick();
    return () => {
      disposed = true;
      clearInterval(interval);
      window.removeEventListener('online', reconnect);
      active.current?.controller.abort();
    };
  }, [scope, input.enabled]);

  async function enqueue(
    files: File[],
    target: { eventId: string; albumId: string | null },
    caption: string,
  ) {
    if (!scope) throw new Error('Upload storage is initializing.');
    await enqueuePersistentUploads(
      files.map((file) => ({
        id: crypto.randomUUID(),
        scope,
        file,
        fileName: file.name,
        mimeType: file.type,
        ...target,
        caption,
        parts: [],
        status: 'queued',
        progress: 0,
        attempts: 0,
        retryAt: 0,
        leaseUntil: 0,
        createdAt: Date.now(),
      })),
    );
    setUploads(await listPersistentUploads(scope));
  }
  async function setStatus(id: string, status: 'queued' | 'paused') {
    if (active.current?.id === id) active.current.controller.abort();
    await updatePersistentUpload(id, {
      status,
      leaseOwner: undefined,
      leaseUntil: 0,
      attempts: 0,
      retryAt: 0,
    });
    if (scope) setUploads(await listPersistentUploads(scope));
  }
  async function remove(id: string) {
    if (active.current?.id === id) active.current.controller.abort();
    await removePersistentUpload(id);
    if (scope) setUploads(await listPersistentUploads(scope));
  }
  return { uploads, enqueue, setStatus, remove };
}
